import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import {
  LeadActivityUiModel,
  LeadCommentUiModel,
  LeadUiModel
} from '../../../shared/ui-models/lead.ui-model';
import { LeadConfigModel } from '../../../shared/models/lead.model';
import {
  addLeadComment,
  changeLeadStatus,
  getLeadById,
  getLeadComments,
  getLeadConfig,
  getLeadTimeline,
  recordContactAttempt
} from '../../../shared/services/data/lead.data';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { Loader } from '../../../shared/components/core-ui/loader/loader.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { SlaBadge } from '../../shared/sla-badge.component';
import { StatusModal } from './components/status-modal.component';

const titleCase = (value: string): string =>
  value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div className="py-1">
    <div className="text-xs uppercase text-gray-400">{label}</div>
    <div className="text-sm">{value || '—'}</div>
  </div>
);

export const LeadDetailsContainer: React.FC = () => {
  const { leadId } = useParams<{ leadId: string }>();
  const [lead, setLead] = useState<LeadUiModel | undefined>();
  const [timeline, setTimeline] = useState<LeadActivityUiModel[]>([]);
  const [comments, setComments] = useState<LeadCommentUiModel[]>([]);
  const [config, setConfig] = useState<LeadConfigModel | undefined>();
  const [loading, setLoading] = useState(true);
  const [showStatus, setShowStatus] = useState(false);
  const [draftComment, setDraftComment] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([
      getLeadById(leadId),
      getLeadTimeline(leadId),
      getLeadComments(leadId),
      getLeadConfig()
    ]).then(([leadResult, timelineResult, commentsResult, configResult]) => {
      if (!isErrorInfo(leadResult)) setLead(leadResult);
      if (!isErrorInfo(timelineResult)) setTimeline(timelineResult);
      if (!isErrorInfo(commentsResult)) setComments(commentsResult);
      if (!isErrorInfo(configResult)) setConfig(configResult);
      setLoading(false);
    });
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatus = (payload: Record<string, unknown>) => {
    const promise = changeLeadStatus(leadId, payload).then((response: LeadUiModel | ErrorInfo) => {
      if (isErrorInfo(response)) throw response;
      setShowStatus(false);
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: 'Updating lead...',
      successMessage: 'Lead updated',
      errorPermissionMessage: 'You do not have permission to change this lead',
      errorFailedMessage: 'Failed to update lead'
    });
  };

  const handleAttempt = () => {
    const promise = recordContactAttempt(leadId).then((response: LeadUiModel | ErrorInfo) => {
      if (isErrorInfo(response)) throw response;
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: 'Recording attempt...',
      successMessage: 'Attempt recorded',
      errorPermissionMessage: 'You do not have permission to record an attempt',
      // Spending the last attempt auto-resolves the lead as unreachable, and
      // any further attempt is refused until someone reopens it.
      errorFailedMessage: 'Could not record — the lead may be resolved already'
    });
  };

  const handleComment = () => {
    const text = draftComment.trim();
    if (!text) return;

    const promise = addLeadComment(leadId, text).then((response: string | ErrorInfo) => {
      if (isErrorInfo(response)) throw response;
      setDraftComment('');
      return load();
    });

    birdToast.promise(promise, {
      loadingMessage: 'Adding comment...',
      successMessage: 'Comment added',
      errorPermissionMessage: 'You do not have permission to comment',
      errorFailedMessage: 'Failed to add comment'
    });
  };

  if (loading && !lead) return <Loader />;
  if (!lead) return <p className="p-8 text-center text-gray-500">Lead not found.</p>;

  const outOfAttempts = lead.remainingContactAttempts <= 0;

  return (
    <div className="flex h-full flex-col items-stretch md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">
          #{lead.shortId} {lead.name || 'Unnamed lead'}
        </h1>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs">{titleCase(lead.status)}</span>
        <SlaBadge state={lead.slaState} />
        <span className="ml-auto flex gap-2">
          <Button type={ButtonType.gray} disabled={outOfAttempts} onClick={handleAttempt}>
            {outOfAttempts
              ? 'No attempts left'
              : `Log attempt (${lead.remainingContactAttempts} left)`}
          </Button>
          <Button type={ButtonType.primary} onClick={() => setShowStatus(true)}>
            Change status
          </Button>
        </span>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded bg-white p-4 shadow-sm md:col-span-1">
          <h2 className="mb-2 text-sm font-medium">Contact</h2>
          <Field label="Phone" value={lead.phone} />
          <Field label="Email" value={lead.email} />
          <Field label="Vertical" value={titleCase(lead.vertical)} />
          <Field label="Source" value={titleCase(lead.source)} />
          <Field label="Assignee" value={lead.assigneeName ?? 'Unassigned'} />
          <Field label="Value" value={lead.valueCategory} />
          <Field label="Message" value={lead.message} />

          <h2 className="mb-2 mt-4 text-sm font-medium">Attribution</h2>
          <Field label="Marketing channel" value={lead.marketingChannel} />
          <Field label="Campaign" value={lead.utmCampaign} />
          <Field label="Source / medium" value={[lead.utmSource, lead.utmMedium].filter(Boolean).join(' / ')} />
          <Field label="First touch" value={lead.firstTouchChannel} />
        </div>

        <div className="rounded bg-white p-4 shadow-sm md:col-span-1">
          <h2 className="mb-2 text-sm font-medium">Timeline</h2>
          {timeline.length === 0 ? (
            <p className="text-sm text-gray-500">Nothing recorded yet.</p>
          ) : (
            timeline.map((activity) => (
              <div key={activity.id} className="border-b border-gray-100 py-2">
                <div className="text-sm">{activity.description}</div>
                <div className="text-xs text-gray-400">
                  {activity.isSystem ? 'System' : activity.actorName}
                  {' · '}
                  {new Date(activity.createdAt).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="rounded bg-white p-4 shadow-sm md:col-span-1">
          <h2 className="mb-2 text-sm font-medium">Comments</h2>
          <textarea
            className="mb-2 w-full rounded border border-gray-300 px-3 py-2"
            rows={3}
            placeholder="Add a comment"
            value={draftComment}
            onChange={(event) => setDraftComment(event.target.value)}
          />
          <Button
            type={ButtonType.primary}
            disabled={draftComment.trim().length === 0}
            onClick={handleComment}
          >
            Comment
          </Button>

          <div className="mt-3">
            {comments.length === 0 ? (
              <p className="text-sm text-gray-500">No comments.</p>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="border-b border-gray-100 py-2">
                  <div className="text-sm">{comment.text}</div>
                  <div className="text-xs text-gray-400">
                    {comment.authorName} · {new Date(comment.createdAt).toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {config ? (
        <StatusModal
          show={showStatus}
          lead={lead}
          config={config}
          onClose={() => setShowStatus(false)}
          onSubmit={handleStatus}
        />
      ) : null}
    </div>
  );
};
